import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, UntypedFormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';

import {responseCallbackToTreeNodes} from '../../../../+organizations/org-tree.service';
import {SelectedTreeItem} from '../../../../+reports/reports.interfaces';
import {FilterOperator} from '../../../../app.constants';
import {AnyData, ApiListAnyData, ApiService} from '../../../api';
import {TreeNode} from '../../../ui';
import {CriterionDescriptor, Entity, PopulationCriteria, PopulationCriterion} from '../create-population.interfaces';
import {CreatePopulationService} from '../create-population.service';


@Component({
  selector: 'gp-population-entity-field',
  templateUrl: './population-entity-field.component.html',
  styleUrls: ['./population-entity-field.component.css']
})
export class PopulationEntityFieldComponent implements OnInit {

  @Input()
  public criterion: CriterionDescriptor;

  @Input()
  public populationForm: UntypedFormGroup;

  @Input()
  public index: number;

  @Input()
  public populationCriteriaCookie: PopulationCriteria;

  @Output()
  public selectedEntityNameAvailable = new EventEmitter<string>();

  @Input()
  public selectedOperator!: string | undefined;

  public makeAllNodesSelectable: boolean;

  public tree: Array<TreeNode<AnyData>>;

  public lookupMap: Map<string | number , TreeNode<AnyData>> = new Map<string | number , TreeNode<AnyData>>();

  public url: string;

  public lazyValue: string;

  constructor(private createPopulationService: CreatePopulationService, public api: ApiService) {
  }

  ngOnInit() {
    if (this.makeAllNodesSelectable !== undefined && this.makeAllNodesSelectable !== this.isEqualsWithChildrenOperator()) {
      this.clearTreeSelection();
    }

    this.makeAllNodesSelectable = this.isEqualsWithChildrenOperator();

    this.setEntityLazyValue();
    this.loadTree(this.criterion?.metadatas?.rel, this.responseCallback, this.criterion?.zoneID === 'GP_WORKFLOW_FALLBACK_RECIPIENT');
  }

  ngOnChanges() {
    if (this.makeAllNodesSelectable !== undefined && this.makeAllNodesSelectable !== this.isEqualsWithChildrenOperator()) {
      this.clearTreeSelection();
    }

    this.makeAllNodesSelectable = this.isEqualsWithChildrenOperator();
    this.tree = [];
    this.lookupMap = new Map<string | number , TreeNode<AnyData>>();
    this.lazyValue = null;
    this.selectedEntityNameAvailable.emit(null);

    this.loadTree(this.criterion?.metadatas?.rel, this.responseCallback, this.criterion?.zoneID === 'GP_WORKFLOW_FALLBACK_RECIPIENT');
  }

  private isEqualsWithChildrenOperator(): boolean {
    return this.selectedOperator === FilterOperator.EQUALS_WITH_CHILDREN;
  }

  public clearTreeSelection(): void {
    const criteriaArray = this.populationForm.get('criteria') as FormArray;
    criteriaArray.at(this.index).get('criterionValue')?.reset(null);
  }

  public loadTree(url: string, responseCallBack?: (response: AnyData, lookupMap: Map<string | number ,
      TreeNode<AnyData>>, isDefaultRecipient?: boolean) => TreeNode<AnyData>[], isDefaultRecipient?: boolean): void {
    this.api.get(url).subscribe(response => {
      if (responseCallBack) {
        this.tree = responseCallBack(response, this.lookupMap, isDefaultRecipient );
      } else {
        const list: ApiListAnyData = response;
        const treeRoots: Array<TreeNode<AnyData>> = [];

        list.data.forEach((item: AnyData) => this.lookupMap.set(item.id, {
          text: item.attributes.name,
          value: item.id < 0 ? -item.id : item.id,
          data: item,
          selectable: true,
          visible: true,
          children: []
        }));

        for (const item of Array.from(this.lookupMap.values())) {
          const parent: TreeNode<AnyData> = this.lookupMap.get(item.data.attributes.parent);
          if (parent !== undefined) {
            parent.children.push(item);
          } else {
            treeRoots.push(item);
          }
        }
        this.tree = treeRoots;
      }

      if (this.makeAllNodesSelectable) {
        this.setAllNodesSelectable(this.tree);
      }
    });
  }

  private setAllNodesSelectable(nodes: Array<TreeNode<AnyData>>): void {
    if (!nodes) { return; }
    for (const node of nodes) {
      node.selectable = true;
      if (node.children && node.children.length > 0) {
        this.setAllNodesSelectable(node.children);
      }
    }
  }

  public responseCallback(response: any, lookupMap: Map<any, any>): TreeNode<any>[] {
    return responseCallbackToTreeNodes(response, lookupMap);
  }

  public setEntityLazyValue() {
    if (this.criterion) {
      this.createPopulationService.getEntityList(this.criterion.metadatas.rel).pipe(
        map(entityList => {
          if (entityList.length > 0 && this.populationCriteriaCookie && this.populationCriteriaCookie.criteria) {
            const selectedEntityCriterion: PopulationCriterion = this.populationCriteriaCookie.criteria
              .find(criterion => criterion.zoneType === this.criterion.type && criterion.zoneName === this.criterion.zoneID);
            if (selectedEntityCriterion) {
              const selectedEntity: Entity = entityList.find(entity => entity.id === selectedEntityCriterion.values[0]);
              this.lazyValue = selectedEntity ? selectedEntity.name : null;
              this.selectedEntityNameAvailable.emit(this.lazyValue);
            }
          }
        })
      )
        .subscribe();
    }
  }

  public onEntitySelected(item: SelectedTreeItem) {
    this.selectedEntityNameAvailable.emit(item.text);
  }
}
